import React, { useEffect, useState, useCallback } from "react";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "@fortawesome/fontawesome-free/css/all.min.css";
import { useNavigate } from "react-router-dom";
import { getToken, getAuthUser, clearAuth } from "../auth";

const API = "https://api.vyapakprint.com/api";

const defaultStats = {
  Pending: 0,
  Printing: 0,
  Packing: 0,
  Dispatched: 0,
  Cancelled: 0,
};

const getStatsStorageKey = (userKey) => `vyapak_order_stats_${userKey}`;
const getSeenChangesKey = (userKey) =>
  `vyapak_seen_order_stat_changes_${userKey}`;

const Home = () => {
  const [productCount, setProductCount] = useState(0);
  const [banners, setBanners] = useState([]);
  const [distributionId, setDistributionId] = useState(null);
  const [orderStats, setOrderStats] = useState(defaultStats);
  const [changedStats, setChangedStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [mustForcePasswordChange, setMustForcePasswordChange] = useState(false);
  const [copyPasswordValue, setCopyPasswordValue] = useState("");
  const [passwordForm, setPasswordForm] = useState({
    newPassword: "",
    confirmPassword: "",
  });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState("");

  const navigate = useNavigate();

  const getStoredStats = (userKey) => {
    if (!userKey) return null;
    try {
      const raw = localStorage.getItem(getStatsStorageKey(userKey));
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  };

  const getSeenChanges = (userKey) => {
    if (!userKey) return {};
    try {
      const raw = localStorage.getItem(getSeenChangesKey(userKey));
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  };

  const saveSeenChanges = (userKey, seenObj) => {
    if (!userKey) return;
    try {
      localStorage.setItem(getSeenChangesKey(userKey), JSON.stringify(seenObj));
    } catch (err) {
      console.error("Failed to save seen changes:", err);
    }
  };

  const saveStats = (userKey, statsObj) => {
    if (!userKey) return;
    try {
      localStorage.setItem(
        getStatsStorageKey(userKey),
        JSON.stringify(statsObj)
      );
    } catch (err) {
      console.error("Failed to save stats:", err);
    }
  };

  const detectChangedStats = useCallback((userKey, newStats) => {
    if (!userKey) {
      setChangedStats({});
      return;
    }

    const prevStats = getStoredStats(userKey);
    const seenChanges = getSeenChanges(userKey);

    if (!prevStats) {
      saveStats(userKey, newStats);
      setChangedStats({});
      return;
    }

    const changed = {};

    Object.keys(defaultStats).forEach((key) => {
      const oldValue = Number(prevStats?.[key] || 0);
      const newValue = Number(newStats?.[key] || 0);

      if (oldValue !== newValue) {
        changed[key] = {
          oldValue,
          newValue,
          direction: newValue > oldValue ? "up" : "down",
          seen: false,
        };
      } else if (seenChanges?.[key] && seenChanges[key].seen === false) {
        changed[key] = seenChanges[key];
      }
    });

    setChangedStats(changed);
    saveStats(userKey, newStats);
    saveSeenChanges(userKey, changed);
  }, []);

  const normalizeOrdersArray = (ordersData) => {
    if (Array.isArray(ordersData)) return ordersData;
    if (Array.isArray(ordersData?.BusinessOrders)) return ordersData.BusinessOrders;
    if (Array.isArray(ordersData?.orders)) return ordersData.orders;
    if (Array.isArray(ordersData?.data)) return ordersData.data;
    return [];
  };

  const normalizeDistributionArray = (distributionData) => {
    if (Array.isArray(distributionData)) return distributionData;
    if (Array.isArray(distributionData?.data)) return distributionData.data;
    if (Array.isArray(distributionData?.distributions))
      return distributionData.distributions;
    return [];
  };

  const extractUserId = (order) => {
    return String(
      order?.userId?._id ||
        order?.userId?.id ||
        order?.userId ||
        order?.userDetails?._id ||
        ""
    );
  };

  const shouldForcePasswordChange = (user) => {
    const hasField = Object.prototype.hasOwnProperty.call(
      user || {},
      "copypassword"
    );

    if (!hasField) return true;

    const value = user?.copypassword;

    if (value === null || value === undefined) return true;

    const normalized = String(value).trim().toLowerCase();

    return (
      normalized === "" ||
      normalized === "n/a" ||
      normalized === "na" ||
      normalized === "null" ||
      normalized === "undefined" ||
      normalized === "not found" ||
      normalized === "notfound"
    );
  };

  const openForcedPasswordModal = (matchedUser) => {
    const cp = String(matchedUser?.copypassword || "").trim();

    setCopyPasswordValue(cp);
    setMustForcePasswordChange(true);
    setShowPasswordModal(true);
    setPasswordError("");
    setPasswordSuccess("");
    setPasswordForm({
      newPassword: "",
      confirmPassword: "",
    });

    localStorage.setItem("mustChangePassword", "true");
    localStorage.setItem("copyPasswordUserId", String(matchedUser?._id || ""));
  };

  const closeForcedPasswordModal = () => {
    if (mustForcePasswordChange) return;
    setShowPasswordModal(false);
  };

  const handlePasswordInput = (e) => {
    const { name, value } = e.target;
    setPasswordForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const submitPasswordChange = async (e) => {
    e.preventDefault();

    const token = getToken();

    if (!distributionId) {
      setPasswordError("User id not found.");
      return;
    }

    const currentCopyPassword = String(copyPasswordValue || "").trim();
    const newPassword = String(passwordForm.newPassword || "").trim();
    const confirmPassword = String(passwordForm.confirmPassword || "").trim();

    if (!newPassword) {
      setPasswordError("New password is required.");
      return;
    }

    if (newPassword.length < 6) {
      setPasswordError("New password must be at least 6 characters.");
      return;
    }

    if (currentCopyPassword && newPassword === currentCopyPassword) {
      setPasswordError("New password must be different from current password.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError("Confirm password does not match.");
      return;
    }

    try {
      setPasswordLoading(true);
      setPasswordError("");
      setPasswordSuccess("");

      const res = await fetch(
        `${API}/distribution/${distributionId}/change-password`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            newPassword,
            confirmPassword,
          }),
        }
      );

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(data?.message || "Failed to change password.");
      }

      setPasswordSuccess("Password changed successfully.");
      setMustForcePasswordChange(false);
      setShowPasswordModal(false);
      setCopyPasswordValue("");

      localStorage.removeItem("mustChangePassword");
      localStorage.removeItem("copyPasswordUserId");

      setPasswordForm({
        newPassword: "",
        confirmPassword: "",
      });

      await fetchHomeData(true);
    } catch (err) {
      setPasswordError(err?.message || "Failed to change password.");
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleLogout = () => {
    if (!window.confirm("Are you sure you want to logout?")) return;
    clearAuth();
    navigate("/login", { replace: true });
  };

  const fetchHomeData = useCallback(
    async (isManualRefresh = false) => {
      const token = getToken();
      const auth = getAuthUser();
      const email = String(auth?.email || "")
        .trim()
        .toLowerCase();
      const fallbackUserKey = email || "guest";

      try {
        if (isManualRefresh) {
          setRefreshing(true);
        } else {
          setLoading(true);
        }

        const bannerPromise = fetch(`${API}/banner/getBusinessBanners`).then(
          (res) => res.json()
        );

        const productPromise = fetch(
          `${API}/businessProducts/getAllBusinessProducts`
        ).then((res) => res.json());

        const [bannerData, productData] = await Promise.all([
          bannerPromise,
          productPromise,
        ]);

        setBanners(
          Array.isArray(bannerData)
            ? [...bannerData].sort((a, b) => (a?.index || 0) - (b?.index || 0))
            : []
        );

        setProductCount(Array.isArray(productData) ? productData.length : 0);

        if (!token || !email) {
          setDistributionId(null);
          setOrderStats(defaultStats);
          setChangedStats({});
          setShowPasswordModal(false);
          setMustForcePasswordChange(false);
          return;
        }

        const distributionRes = await fetch(`${API}/distribution/`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!distributionRes.ok) {
          throw new Error(
            `Failed to fetch distribution data: ${distributionRes.status}`
          );
        }

        const distributionData = await distributionRes.json();
        const distributionList = normalizeDistributionArray(distributionData);

        const matchedUser =
          distributionList.find(
            (item) =>
              String(item?.email || "")
                .trim()
                .toLowerCase() === email
          ) || null;

        if (!matchedUser?._id) {
          setDistributionId(null);
          setOrderStats(defaultStats);
          detectChangedStats(fallbackUserKey, defaultStats);
          setShowPasswordModal(false);
          setMustForcePasswordChange(false);
          return;
        }

        const currentUserKey = String(matchedUser._id);
        setDistributionId(currentUserKey);

        if (shouldForcePasswordChange(matchedUser)) {
          openForcedPasswordModal(matchedUser);
        } else {
          setShowPasswordModal(false);
          setMustForcePasswordChange(false);
          setCopyPasswordValue("");
        }

        const ordersRes = await fetch(
          `${API}/businessOrders/getAllBusinessOrders`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!ordersRes.ok) {
          throw new Error(`Failed to fetch orders: ${ordersRes.status}`);
        }

        const ordersData = await ordersRes.json();
        const allOrders = normalizeOrdersArray(ordersData);

        const mine = allOrders.filter(
          (order) => extractUserId(order) === currentUserKey
        );

        const cnt = mine.reduce(
          (acc, order) => {
            const status = String(order?.status || "").trim();
            if (status && Object.prototype.hasOwnProperty.call(acc, status)) {
              acc[status] += 1;
            }
            return acc;
          },
          { ...defaultStats }
        );

        setOrderStats(cnt);
        detectChangedStats(currentUserKey, cnt);

        console.log("auth email:", email);
        console.log("matched distribution user:", matchedUser);
        console.log("matched distribution user id:", matchedUser?._id);
        console.log("copypassword:", matchedUser?.copypassword);
        console.log("all orders:", allOrders);
        console.log("my filtered orders:", mine);
        console.log("final stats:", cnt);
      } catch (err) {
        console.error("Home page fetch error:", err);
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [detectChangedStats]
  );

  useEffect(() => {
    fetchHomeData();

    const interval = setInterval(() => {
      fetchHomeData(true);
    }, 30000);

    return () => clearInterval(interval);
  }, [fetchHomeData]);

  const markAsSeen = (statusKey) => {
    const auth = getAuthUser();
    const email = String(auth?.email || "")
      .trim()
      .toLowerCase();
    const currentUserKey = distributionId || email || "guest";

    setChangedStats((prev) => {
      if (!prev[statusKey]) return prev;

      const updated = { ...prev };
      delete updated[statusKey];
      saveSeenChanges(currentUserKey, updated);
      return updated;
    });
  };

  const handlePlaceOrder = () => {
    if (mustForcePasswordChange) {
      setShowPasswordModal(true);
      return;
    }
    navigate("/products");
  };

  const handleStatusClick = (status) => {
    if (mustForcePasswordChange) {
      setShowPasswordModal(true);
      return;
    }
    markAsSeen(status);
    navigate(`/order-status?status=${encodeURIComponent(status)}`);
  };

  const cards = [
    {
      key: "place",
      title: "Place Order",
      description: "नया ऑर्डर यहाँ पर दें।",
      icon: "fas fa-plus-circle",
      color: "bg-blue-500",
      onClick: handlePlaceOrder,
    },
    {
      key: "Pending",
      title: "Pending",
      count: orderStats.Pending,
      description: "Awaiting processing",
      icon: "fas fa-hourglass-half",
      color: "bg-yellow-500",
      onClick: () => handleStatusClick("Pending"),
      isHighlight: !!changedStats.Pending,
      changeInfo: changedStats.Pending,
    },
    {
      key: "Printing",
      title: "Printing",
      count: orderStats.Printing,
      description: "Jobs in print",
      icon: "fas fa-print",
      color: "bg-indigo-500",
      onClick: () => handleStatusClick("Printing"),
      isHighlight: !!changedStats.Printing,
      changeInfo: changedStats.Printing,
    },
    {
      key: "Packing",
      title: "Packing",
      count: orderStats.Packing,
      description: "Being packed",
      icon: "fas fa-box-open",
      color: "bg-purple-500",
      onClick: () => handleStatusClick("Packing"),
      isHighlight: !!changedStats.Packing,
      changeInfo: changedStats.Packing,
    },
    {
      key: "Dispatched",
      title: "Dispatched",
      count: orderStats.Dispatched,
      description: "On the way",
      icon: "fas fa-truck-moving",
      color: "bg-green-500",
      onClick: () => handleStatusClick("Dispatched"),
      isHighlight: !!changedStats.Dispatched,
      changeInfo: changedStats.Dispatched,
    },
    {
      key: "Cancelled",
      title: "Cancelled",
      count: orderStats.Cancelled,
      description: "Orders cancelled",
      icon: "fas fa-times-circle",
      color: "bg-red-500",
      onClick: () => handleStatusClick("Cancelled"),
      isHighlight: !!changedStats.Cancelled,
      changeInfo: changedStats.Cancelled,
    },
  ];

  const carouselSettings = {
    dots: true,
    infinite: banners.length > 1,
    autoplay: banners.length > 1,
    autoplaySpeed: 4000,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: true,
  };

  return (
    <>
      <div className="container mx-auto p-4 sm:p-6">
        <div className="mb-8">
          <Slider {...carouselSettings}>
            {banners.length
              ? banners.map(({ _id, url }, i) => (
                  <div key={_id ?? i}>
                    <div className="flex w-full items-center justify-center bg-gray-100">
                      <img
                        src={url}
                        alt={`Banner ${i + 1}`}
                        className="block h-[160px] w-full object-contain sm:h-[220px] md:h-[320px] lg:h-[420px]"
                      />
                    </div>
                  </div>
                ))
              : [1, 2].map((n) => (
                  <div key={n}>
                    <div className="flex h-[160px] items-center justify-center bg-gray-200 sm:h-[220px] md:h-[320px] lg:h-[420px]">
                      <h2 className="px-4 text-center text-base font-semibold sm:text-xl md:text-2xl">
                        {`Carousel Slide ${n}`}
                      </h2>
                    </div>
                  </div>
                ))}
          </Slider>
        </div>

        {loading ? (
          <div className="py-16 text-center text-gray-500">
            Loading dashboard...
          </div>
        ) : (
          <div className="mb-8 grid grid-cols-1 gap-4 sm:gap-6 md:grid-cols-2 lg:grid-cols-3">
            {cards.map((card) => (
              <div
                key={card.key}
                onClick={card.onClick}
                className={`
                  relative flex cursor-pointer items-center rounded-lg p-5 text-white shadow-lg transition-all duration-300 sm:p-6
                  ${card.color}
                  ${
                    card.isHighlight
                      ? "scale-[1.03] animate-pulse ring-4 ring-white/90 shadow-2xl"
                      : "hover:opacity-95"
                  }
                `}
              >
                {card.isHighlight && (
                  <span className="absolute right-2 top-2 rounded-full bg-white px-2 py-1 text-[10px] font-bold text-black">
                    Updated
                  </span>
                )}

                <i className={`${card.icon} mr-4 text-3xl`} />

                <div className="min-w-0">
                  <h3 className="text-xl font-semibold">{card.title}</h3>
                  <p className="text-sm text-white/90">{card.description}</p>

                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    {card.count > 0 && (
                      <p className="text-2xl font-bold">{card.count}</p>
                    )}

                    {card.isHighlight && card.changeInfo && (
                      <span className="rounded bg-black/20 px-2 py-1 text-xs font-semibold">
                        {card.changeInfo.oldValue} → {card.changeInfo.newValue}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showPasswordModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 px-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
            <div className="mb-4">
              <h2 className="text-2xl font-bold text-gray-900">
                Change Password
              </h2>
              <p className="mt-2 text-sm text-gray-600">
                Your account is still using old password. Please change it
                before continuing.
              </p>
            </div>

            <form onSubmit={submitPasswordChange} className="space-y-4">
              {copyPasswordValue ? (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                  A password change is required for this account.
                </div>
              ) : null}

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  New Password
                </label>
                <input
                  type="password"
                  name="newPassword"
                  value={passwordForm.newPassword}
                  onChange={handlePasswordInput}
                  className="w-full rounded-lg border border-gray-300 px-4 py-3 outline-none focus:border-blue-500"
                  placeholder="Enter new password"
                  required
                />
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Confirm Password
                </label>
                <input
                  type="password"
                  name="confirmPassword"
                  value={passwordForm.confirmPassword}
                  onChange={handlePasswordInput}
                  className="w-full rounded-lg border border-gray-300 px-4 py-3 outline-none focus:border-blue-500"
                  placeholder="Confirm new password"
                  required
                />
              </div>

              {passwordError ? (
                <p className="text-sm font-medium text-red-600">
                  {passwordError}
                </p>
              ) : null}

              {passwordSuccess ? (
                <p className="text-sm font-medium text-green-600">
                  {passwordSuccess}
                </p>
              ) : null}

              <div className="flex items-center gap-3 pt-2">
                <button
                  type="button"
                  onClick={handleLogout}
                  className="w-1/3 rounded-lg border border-red-300 bg-red-50 px-4 py-3 font-semibold text-red-600 hover:bg-red-100"
                >
                  Logout
                </button>

                {!mustForcePasswordChange && (
                  <button
                    type="button"
                    onClick={closeForcedPasswordModal}
                    className="w-1/3 rounded-lg border border-gray-300 px-4 py-3 font-semibold text-gray-700"
                  >
                    Cancel
                  </button>
                )}

                <button
                  type="submit"
                  disabled={passwordLoading}
                  className={`rounded-lg bg-blue-600 px-4 py-3 font-semibold text-white ${
                    mustForcePasswordChange ? "w-2/3" : "w-1/3"
                  } disabled:opacity-60`}
                >
                  {passwordLoading ? "Updating..." : "Update"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default Home;